import React from "react";
import { useNavigate } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import { ArrowRight, Play } from "lucide-react";
import { LIBRARY_VIDEOS } from "@/data/biblioteca";

// Editorial magazine-style preview of the Biblioteca Lu.ft.
// Not a video grid — an asymmetric composition that hints at the library.
export default function BibliotecaPreview() {
  const ref = useReveal();
  const navigate = useNavigate();

  const [featured, second, third] = LIBRARY_VIDEOS;

  return (
    <section
      id="biblioteca"
      ref={ref}
      className="relative overflow-hidden bg-[var(--luft-bg-alt)] py-32 sm:py-44 lg:py-52"
      data-testid="biblioteca-preview-section"
    >
      {/* soft sky wash */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(199,213,222,0.35) 45%, transparent 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16 items-end mb-20 reveal">
          <div className="col-span-12 lg:col-span-7">
            <p className="eyebrow mb-8">04 — Biblioteca Lu.ft</p>
            <h2
              className="editorial-heading text-[var(--luft-ink)] text-[clamp(2.4rem,6vw,5rem)] leading-[0.98]"
              data-testid="biblioteca-title"
            >
              A tua prática,<br />
              <em className="editorial-italic">onde estiveres</em>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="font-body font-light text-[var(--luft-ink-soft)] text-[1.02rem] leading-relaxed max-w-md">
              Práticas curtas para os dias em que fica em casa, para levar
              consigo quando viaja ou simplesmente para manter o corpo em
              movimento entre aulas. Uma biblioteca em crescimento, criada com
              o mesmo cuidado e atenção do estúdio Lu.ft.
            </p>
          </div>
        </div>

        {/* Editorial asymmetric composition */}
        <div className="relative grid grid-cols-12 gap-y-8 lg:gap-x-8">
          {/* Featured / large */}
          <article
            className="col-span-12 md:col-span-8 relative reveal"
            data-testid={`biblioteca-preview-${featured.id}`}
          >
            <div
              className="relative overflow-hidden group cursor-pointer"
              style={{ borderRadius: "var(--radius-organic)" }}
              onClick={() => navigate("/online")}
            >
              <img
                src={featured.poster}
                alt={featured.title}
                loading="lazy"
                className="w-full aspect-[16/11] object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50 pointer-events-none" />

              <span
                className="absolute top-8 left-8 font-display italic text-white text-3xl"
                aria-hidden="true"
              >
                i
              </span>

              <span className="absolute top-8 right-8 w-14 h-14 rounded-full border border-white/60 bg-white/8 backdrop-blur-md flex items-center justify-center transition-all duration-700 group-hover:bg-white group-hover:border-white">
                <Play
                  size={16}
                  strokeWidth={1.2}
                  className="text-white ml-0.5 transition-colors duration-500 group-hover:text-[var(--luft-ink)]"
                  fill="currentColor"
                />
              </span>

              <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between">
                <div>
                  <p
                    className="text-white/70 mb-2"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "0.58rem",
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                    }}
                  >
                    {featured.duration} · {featured.level}
                  </p>
                  <h3 className="editorial-heading text-white text-3xl sm:text-4xl">
                    {featured.title}
                  </h3>
                </div>
              </div>
            </div>
          </article>

          {/* Small side card — sits offset */}
          <article
            className="col-span-12 md:col-span-4 md:mt-24 relative reveal"
            data-testid={`biblioteca-preview-${second.id}`}
          >
            <div
              className="relative overflow-hidden group cursor-pointer"
              style={{ borderRadius: "var(--radius-organic)" }}
              onClick={() => navigate("/online")}
            >
              <img
                src={second.poster}
                alt={second.title}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
                style={{ filter: "grayscale(0.15)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55 pointer-events-none" />

              <span
                className="absolute top-6 left-6 font-display italic text-white text-2xl"
                aria-hidden="true"
              >
                ii
              </span>

              <div className="absolute bottom-6 left-6 right-6">
                <p
                  className="text-white/70 mb-1.5"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "0.56rem",
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                  }}
                >
                  {second.duration}
                </p>
                <h3 className="editorial-heading text-white text-2xl">
                  {second.title}
                </h3>
              </div>
            </div>
          </article>

          {/* Third — small floating below, offset */}
          <article
            className="col-span-12 md:col-span-5 md:col-start-4 -mt-4 md:-mt-16 relative reveal"
            data-testid={`biblioteca-preview-${third.id}`}
          >
            <div className="flex items-center gap-6 md:gap-8">
              <div
                className="relative overflow-hidden group cursor-pointer flex-shrink-0"
                style={{ borderRadius: "var(--radius-organic)", width: "clamp(120px, 22vw, 200px)" }}
                onClick={() => navigate("/online")}
              >
                <img
                  src={third.poster}
                  alt={third.title}
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-black/15" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-10 h-10 rounded-full border border-white/60 bg-white/10 backdrop-blur-md flex items-center justify-center">
                    <Play
                      size={12}
                      strokeWidth={1.2}
                      className="text-white ml-0.5"
                      fill="currentColor"
                    />
                  </span>
                </span>
              </div>
              <div>
                <p
                  className="text-[var(--luft-ink-soft)] mb-1.5"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "0.58rem",
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                  }}
                >
                  iii · {third.duration}
                </p>
                <h3 className="editorial-heading text-[var(--luft-ink)] text-2xl">
                  {third.title}
                </h3>
                <p className="mt-2 font-body font-light text-[var(--luft-ink-soft)] text-sm max-w-xs leading-relaxed">
                  {third.description}
                </p>
              </div>
            </div>
          </article>
        </div>

        {/* CTA */}
        <div className="mt-24 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 reveal">
          <p className="font-display italic text-2xl sm:text-3xl text-[var(--luft-ink-soft)] max-w-lg leading-snug">
            Uma biblioteca em crescimento,<br />
            para continuar a prática entre aulas.
          </p>
          <button
            onClick={() => navigate("/online")}
            className="inline-flex items-center gap-3 self-start sm:self-auto text-[var(--luft-ink)] text-[0.72rem] uppercase tracking-[0.28em] border-b border-[var(--luft-ink)]/40 pb-2 hover:text-[var(--luft-blue)] hover:border-[var(--luft-blue)] transition-colors"
            data-testid="biblioteca-explore-cta"
          >
            Ver a biblioteca
            <ArrowRight size={14} strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </section>
  );
}
